/**
 * Executive Intelligence Centre — the single dashboard binding every module
 * to the Central Metrics Service. Drill-downs, forecasting, alerts, morning
 * briefing surface, and configurable thresholds all live here.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Cell, LineChart, Line, PieChart, Pie, Legend,
} from "recharts";
import {
  Sparkles, TrendingUp, TrendingDown, AlertTriangle, ShieldCheck,
  Wallet, GraduationCap, Users, BookOpen, ClipboardCheck, School,
  ChevronRight, RefreshCw, Mail, Loader2, Settings2, X, CalendarDays,
  MapPin, BookOpenCheck, Home, Activity,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ROLE_LABEL } from "../lib/nav";
import { PageHeader, Section, Loading, KPITile, StatusPill, Markdown } from "../components/Common";

const money = (v, symbol = "$") => `${symbol}${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const pct = (v) => `${Number(v || 0).toFixed(1)}%`;
const TONE_COLORS = { on_target: "#059669", monitor: "#D97706", attention: "#dc2626" };
const ALERT_COLORS = { high: "#dc2626", medium: "#D97706", low: "#0284c7" };

function Trend({ direction }) {
  if (direction === "up") return <TrendingUp className="w-4 h-4 text-emerald-600" />;
  if (direction === "down") return <TrendingDown className="w-4 h-4 text-red-600" />;
  return null;
}

function ToneKPI({ label, value, sub, target, tone, trend, testId, currency }) {
  const color = TONE_COLORS[tone] || "#0f172a";
  return (
    <div className="hl-card p-5 flex flex-col gap-1.5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="overline">{label}</div>
        <Trend direction={trend} />
      </div>
      <div className="font-display font-bold tracking-tight text-3xl" style={{ color }}>
        {value}
      </div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
      {target !== undefined && (
        <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">
          Target · {target}
          {currency ? "" : "%"}
        </div>
      )}
    </div>
  );
}

function AlertsRail({ alerts, onSelect }) {
  if (!alerts?.length) {
    return (
      <div className="hl-card p-5 text-sm text-slate-500 flex items-center gap-2" data-testid="alerts-empty">
        <ShieldCheck className="w-4 h-4 text-emerald-500" /> No open executive alerts. All KPIs within tolerance.
      </div>
    );
  }
  return (
    <div className="hl-card p-4 space-y-2" data-testid="alerts-rail">
      {alerts.map((a, i) => (
        <button
          key={i}
          onClick={() => onSelect?.(a.domain)}
          data-testid={`alert-${a.domain}-${a.level}`}
          className="w-full text-left flex items-start gap-3 p-3 rounded-md hover:bg-slate-50 border border-slate-100 transition"
        >
          <span
            className="mt-1 w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ background: ALERT_COLORS[a.level] || "#64748b" }}
          />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-800">{a.title}</div>
            <div className="text-xs text-slate-500">{a.detail}</div>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-400 mt-1 flex-shrink-0" />
        </button>
      ))}
    </div>
  );
}

function DrillDrawer({ domain, onClose, snapshot }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!domain) return;
    setLoading(true);
    const fetcher = {
      finance: api.drillFinance,
      attendance: api.drillAttendance,
      academic: api.drillAcademic,
      admissions: api.drillAdmissions,
      studentlife: api.drillStudentLife,
    }[domain];
    if (!fetcher) return;
    fetcher()
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Unable to load drill-down"))
      .finally(() => setLoading(false));
  }, [domain]);

  if (!domain) return null;
  return (
    <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm animate-fade-in-up" onClick={onClose} data-testid="drill-drawer">
      <div
        className="absolute right-0 top-0 h-full w-full sm:w-[600px] bg-white shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-5 border-b border-slate-200">
          <div>
            <div className="overline text-[color:var(--hl-orange)]">Executive Drill-Down</div>
            <div className="font-display font-bold text-xl capitalize">{domain}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded" data-testid="drill-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-5">
          {loading && <Loading label="Loading drill-down…" />}
          {!loading && data && (
            <DrillContent domain={domain} data={data} snapshot={snapshot} />
          )}
        </div>
      </div>
    </div>
  );
}

function DrillContent({ domain, data, snapshot }) {
  const symbol = snapshot?.config?.currency_symbol || "$";
  if (domain === "finance") {
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <KPITile testId="drill-finance-billed" label="Billed" value={money(data.summary.billed_usd, symbol)} />
          <KPITile testId="drill-finance-collected" tone="dark" label="Collected" value={money(data.summary.collected_usd, symbol)} />
          <KPITile testId="drill-finance-outstanding" label="Outstanding" value={money(data.summary.outstanding_usd, symbol)} sub={`${data.summary.outstanding_alert_count} over ${money(data.summary.outstanding_alert_threshold_usd, symbol)}`} />
          <KPITile testId="drill-finance-overdue" label="Overdue" value={data.summary.overdue_invoice_count} sub={money(data.summary.overdue_amount_usd, symbol)} />
        </div>
        <Section title="Debtor age analysis" subtitle="Outstanding balances bucketed by days overdue">
          <div className="hl-card p-4 h-56">
            <ResponsiveContainer>
              <BarChart data={data.summary.debtor_age_analysis}>
                <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                <XAxis dataKey="bucket" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip formatter={(v) => money(v, symbol)} />
                <Bar dataKey="amount" fill="#D9531E" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
        <Section title="Top debtors" subtitle={`Accounts over ${money(data.summary.outstanding_alert_threshold_usd, symbol)} highlighted`}>
          <div className="hl-card overflow-hidden">
            <table className="hl-table w-full text-sm">
              <thead><tr><th>Student</th><th>Year</th><th className="text-right">Outstanding</th></tr></thead>
              <tbody>
                {(data.summary.top_debtors || []).map((d) => (
                  <tr key={d.student_id} data-testid={`debtor-${d.student_id}`}>
                    <td>
                      <div className="font-semibold">{d.name}</div>
                      <div className="text-xs text-slate-500 mono">{d.admission_no}</div>
                    </td>
                    <td>{d.year_group || "—"}</td>
                    <td className="text-right font-semibold" style={{ color: d.over_threshold ? "#dc2626" : "#0f172a" }}>
                      {money(d.outstanding_usd, symbol)}
                    </td>
                  </tr>
                ))}
                {!data.summary.top_debtors?.length && (
                  <tr><td colSpan={3} className="text-center text-slate-400 py-6">No outstanding balances</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Section>
      </div>
    );
  }
  if (domain === "attendance") {
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <KPITile testId="drill-att-overall" label="Overall" value={pct(data.summary.pct)} sub={`Target ${data.summary.target_pct}%`} />
          <KPITile testId="drill-att-warn" label="Early warnings" value={data.summary.early_warnings} sub={`${data.summary.critical_warnings} critical`} />
        </div>
        <Section title="By year group">
          <div className="hl-card p-4 h-52">
            <ResponsiveContainer>
              <BarChart data={data.by_year}>
                <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                <XAxis dataKey="year" fontSize={11} />
                <YAxis fontSize={11} domain={[70, 100]} />
                <Tooltip formatter={(v) => `${v}%`} />
                <Bar dataKey="attendance_pct" fill="#002147" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
        <Section title="Students at risk">
          <div className="hl-card overflow-hidden">
            <table className="hl-table w-full text-sm">
              <thead><tr><th>Student</th><th>Year</th><th>House</th><th className="text-right">Attendance</th></tr></thead>
              <tbody>
                {(data.at_risk || []).slice(0, 25).map((w) => (
                  <tr key={w.student_id} data-testid={`att-risk-${w.student_id}`}>
                    <td>{w.student_name}</td>
                    <td>{w.year_group}</td>
                    <td>{w.house}</td>
                    <td className="text-right font-semibold" style={{ color: w.risk === "critical" ? "#dc2626" : "#D97706" }}>
                      {pct(w.attendance_pct)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      </div>
    );
  }
  if (domain === "academic") {
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <KPITile testId="drill-ac-avg" label="Grade avg" value={pct(data.summary.grade_avg)} />
          <KPITile testId="drill-ac-risk" label="At-risk" value={data.summary.at_risk_count} sub={`Below ${data.summary.at_risk_threshold}%`} />
          <KPITile testId="drill-ac-top" label="Top performers" value={data.summary.top_performers} sub={`≥ 80%`} />
          <KPITile testId="drill-ac-hw" label="Homework compliance" value={pct(data.summary.homework_compliance_pct)} />
        </div>
        <Section title="Subject averages">
          <div className="hl-card overflow-hidden">
            <table className="hl-table w-full text-sm">
              <thead><tr><th>Subject</th><th>Department</th><th className="text-right">Average</th></tr></thead>
              <tbody>
                {(data.subject_averages || []).map((s) => (
                  <tr key={s.subject_id}>
                    <td>{s.subject_name || s.subject_id}</td>
                    <td className="text-xs text-slate-500">{s.department}</td>
                    <td className="text-right font-semibold">{pct(s.average)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
        {data.at_risk_students?.length > 0 && (
          <Section title="At-risk students">
            <div className="hl-card overflow-hidden">
              <table className="hl-table w-full text-sm">
                <thead><tr><th>Name</th><th>Year</th><th>House</th></tr></thead>
                <tbody>
                  {data.at_risk_students.map((s) => (
                    <tr key={s.id}>
                      <td>{s.first_name} {s.last_name}</td>
                      <td>{s.year_group}</td>
                      <td>{s.house}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}
      </div>
    );
  }
  if (domain === "admissions") {
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <KPITile testId="drill-adm-total" label="Applicants" value={data.summary.total} />
          <KPITile testId="drill-adm-enrolled" tone="dark" label="Enrolled" value={data.summary.enrolled} sub={`${pct(data.summary.conversion_rate)} conversion`} />
          <KPITile testId="drill-adm-boarding" label="Boarding demand" value={data.summary.boarding_demand} />
          <KPITile testId="drill-adm-schol" label="Scholarship pipeline" value={data.summary.scholarship_pipeline} />
        </div>
        <Section title="Recent applicants">
          <div className="hl-card overflow-hidden max-h-96 overflow-y-auto">
            <table className="hl-table w-full text-sm">
              <thead><tr><th>Name</th><th>Year</th><th>Stage</th></tr></thead>
              <tbody>
                {(data.applicants || []).slice(0, 40).map((a) => (
                  <tr key={a.id}>
                    <td>{a.first_name} {a.last_name}</td>
                    <td>{a.year_group_applied}</td>
                    <td><StatusPill status={a.stage} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      </div>
    );
  }
  if (domain === "studentlife") {
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <KPITile testId="drill-life-merits" label="Merits" value={data.summary.merits} />
          <KPITile testId="drill-life-demerits" label="Demerits" value={data.summary.demerits} sub={`Ratio ${data.summary.merit_ratio}`} />
          <KPITile testId="drill-life-awards" label="Awards" value={data.summary.awards} />
          <KPITile testId="drill-life-pastoral" label="Pastoral open" value={data.summary.pastoral_open} />
        </div>
        <Section title="House leaderboard">
          <div className="hl-card p-4 h-48">
            <ResponsiveContainer>
              <BarChart data={data.summary.house_leaderboard} layout="vertical">
                <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                <XAxis type="number" fontSize={11} />
                <YAxis type="category" dataKey="house" width={100} fontSize={11} />
                <Tooltip />
                <Bar dataKey="points" fill="#D9531E" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
        <Section title="Recent events">
          <div className="hl-card overflow-hidden max-h-72 overflow-y-auto">
            <table className="hl-table w-full text-sm">
              <thead><tr><th>Kind</th><th>House</th><th>Title</th><th className="text-right">Points</th></tr></thead>
              <tbody>
                {(data.recent_events || []).map((e) => (
                  <tr key={e.id}>
                    <td><StatusPill status={e.kind === "merit" || e.kind === "achievement" || e.kind === "commendation" ? "positive" : e.kind === "demerit" ? "major" : "minor"} /></td>
                    <td>{e.house || "—"}</td>
                    <td className="text-xs">{e.title}</td>
                    <td className="text-right font-semibold" style={{ color: e.house_points >= 0 ? "#059669" : "#dc2626" }}>{e.house_points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      </div>
    );
  }
  return null;
}

function BriefingCard({ briefing, onGenerate, generating }) {
  return (
    <div className="hl-card hl-ai-card p-6" data-testid="morning-briefing-card">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-[0.24em] text-[color:var(--hl-orange)] font-bold">Principal's Morning Briefing</span>
            {briefing?.email_sent && (
              <span className="text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded uppercase tracking-widest font-semibold flex items-center gap-1">
                <Mail className="w-3 h-3" /> Delivered
              </span>
            )}
          </div>
          <div className="font-display font-bold text-lg text-[color:var(--hl-navy)]">
            {briefing ? briefing.date : "Awaiting today's briefing"}
          </div>
          {briefing && (
            <div className="text-xs text-slate-500 mt-0.5">
              Generated at {new Date(briefing.generated_at).toLocaleString()} · via {briefing.triggered_by}
            </div>
          )}
        </div>
        <button
          onClick={onGenerate}
          disabled={generating}
          data-testid="btn-generate-briefing"
          className="hl-btn-accent inline-flex items-center gap-2 text-sm disabled:opacity-60"
        >
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {generating ? "Generating…" : "Generate now"}
        </button>
      </div>
      {briefing ? (
        <div className="text-sm leading-relaxed text-slate-800 border-l-4 border-[color:var(--hl-orange)] bg-orange-50/40 px-4 py-3 rounded"
             data-testid="briefing-body">
          <Markdown>{briefing.body}</Markdown>
        </div>
      ) : (
        <div className="text-sm text-slate-500">
          The Principal's Morning Briefing runs automatically every day at 07:00 Africa/Harare, pulling live KPIs from the Central Metrics Service and delivered to the leadership team by email. You can also generate one on demand for today.
        </div>
      )}
    </div>
  );
}

function ConfigDialog({ config, onSave, onClose }) {
  const [form, setForm] = useState(config || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(config || {}), [config]);

  const save = async () => {
    setSaving(true);
    try {
      await onSave(form);
      toast.success("Thresholds updated");
      onClose();
    } catch { toast.error("Unable to save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="config-dialog">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-lg">Executive Thresholds</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3 text-sm">
          <label className="block">
            <span className="text-slate-600">Outstanding fee alert (USD)</span>
            <input
              type="number"
              value={form.outstanding_fee_threshold_usd || 5000}
              onChange={(e) => setForm({ ...form, outstanding_fee_threshold_usd: Number(e.target.value) })}
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded"
              data-testid="input-fee-threshold"
            />
          </label>
          <label className="block">
            <span className="text-slate-600">Attendance target (%)</span>
            <input
              type="number"
              value={form.attendance_target_pct || 94}
              onChange={(e) => setForm({ ...form, attendance_target_pct: Number(e.target.value) })}
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded"
              data-testid="input-att-target"
            />
          </label>
          <label className="block">
            <span className="text-slate-600">Collection target (%)</span>
            <input
              type="number"
              value={form.collection_target_pct || 90}
              onChange={(e) => setForm({ ...form, collection_target_pct: Number(e.target.value) })}
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded"
              data-testid="input-collect-target"
            />
          </label>
          <label className="block">
            <span className="text-slate-600">At-risk grade threshold (%)</span>
            <input
              type="number"
              value={form.at_risk_grade_pct || 45}
              onChange={(e) => setForm({ ...form, at_risk_grade_pct: Number(e.target.value) })}
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded"
              data-testid="input-risk-threshold"
            />
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="btn-save-config" className="hl-btn-accent px-4 py-2 text-sm">
            {saving ? "Saving…" : "Save thresholds"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ExecutiveIntelligence() {
  const { tenant, user } = useAuth();
  const [snap, setSnap] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [drill, setDrill] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [showConfig, setShowConfig] = useState(false);

  const reload = () => {
    Promise.all([
      api.execSnapshot().then(({ data }) => setSnap(data)),
      api.briefingLatest().then(({ data }) => setBriefing(data)),
    ]).catch(() => toast.error("Unable to load executive intelligence"));
  };

  useEffect(() => { reload(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.briefingGenerateNow({ send_email: false });
      setBriefing(data);
      toast.success("Morning briefing generated");
    } catch { toast.error("Unable to generate briefing"); }
    finally { setGenerating(false); }
  };

  const saveConfig = async (patch) => {
    const { data } = await api.updateExecConfig(patch);
    setSnap((s) => (s ? { ...s, config: data } : s));
    reload();
  };

  if (!snap) return <Loading label="Loading executive intelligence…" />;
  const symbol = snap.config?.currency_symbol || "$";
  const attTrend = snap.attendance?.trend_14d || [];
  const pipelineBar = Object.entries(snap.admissions?.pipeline || {})
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ stage: k.replace(/_/g, " "), count: v }));
  const houseBar = snap.studentlife?.house_leaderboard || [];

  return (
    <div data-testid="executive-intelligence-page">
      <IntelligenceHero
        tenant={tenant}
        user={user}
        snapshot={snap}
        onReload={reload}
        onConfig={() => setShowConfig(true)}
      />

      {/* Morning briefing card */}
      <div className="mb-6">
        <BriefingCard briefing={briefing} onGenerate={generate} generating={generating} />
      </div>

      {/* Executive KPIs — canonical, period-labelled to prevent
          "same-label / different-period" confusion across surfaces. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="exec-kpi-grid">
        <ToneKPI testId="kpi-students" label="Enrolment" value={snap.people.students} sub={`${snap.people.period_label || "Current roll"} · ${snap.people.boarders} boarders · ${snap.people.day_scholars} day`} />
        <ToneKPI testId="kpi-attendance" label="Attendance" value={pct(snap.attendance.pct)} target={snap.attendance.target_pct} tone={snap.attendance.tone} sub={`${snap.attendance.period_label || "Term to date"} · ${snap.attendance.early_warnings} early warnings`} />
        <ToneKPI testId="kpi-grade" label="Grade average" value={pct(snap.academic.grade_avg)} sub={`${snap.academic.period_label || "Term to date"} · ${snap.academic.at_risk_count} at risk`} />
        <ToneKPI testId="kpi-collection" label="Fee collection" value={pct(snap.finance.collection_pct)} target={snap.finance.target_collection_pct} tone={snap.finance.tone} sub={`${snap.finance.period_label || "Year to date"} · ${money(snap.finance.outstanding_usd, symbol)} outstanding`} />
      </div>

      {/* Alerts rail + Forecast */}
      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2">
          <Section
            title="Executive alerts"
            subtitle={`${snap.alerts.length} open item(s) — click any alert to drill down`}
          >
            <AlertsRail alerts={snap.alerts} onSelect={(d) => setDrill(d)} />
          </Section>
        </div>
        <Section title="Forecast" subtitle="Advisory projections from the metrics service">
          <div className="hl-card p-5 space-y-3" data-testid="forecast-card">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[color:var(--hl-orange)]">Forecast · Projection</span>
              <span className="text-[10px] uppercase tracking-widest text-slate-400">advisory — not current actuals</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-xs text-slate-500 uppercase tracking-widest">Enrolment · next year</div>
              <div className="font-display font-bold text-lg">{snap.forecast.enrolment_next_year}</div>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-xs text-slate-500 uppercase tracking-widest">Collection projection</div>
              <div className="font-display font-bold text-lg">{pct(snap.forecast.collection_projection_pct)}</div>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-xs text-slate-500 uppercase tracking-widest">Revenue 12m</div>
              <div className="font-display font-bold text-lg">{money(snap.forecast.revenue_forecast_12m_usd, symbol)}</div>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-xs text-slate-500 uppercase tracking-widest">Retention risk</div>
              <div className="font-display font-bold text-lg text-red-600">
                {snap.forecast.retention_risk_students} <span className="text-xs text-slate-500 font-normal">({pct(snap.forecast.retention_risk_pct)})</span>
              </div>
            </div>
          </div>
        </Section>
      </div>

      {/* Trend + Admissions + House */}
      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="hl-card p-5 lg:col-span-2" data-testid="attendance-trend">
          <div className="overline mb-1">Attendance last 14 days</div>
          <div className="font-display font-bold text-lg tracking-tight mb-3">Daily attendance</div>
          <div className="h-56">
            <ResponsiveContainer>
              <AreaChart data={attTrend}>
                <defs>
                  <linearGradient id="attGrad" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#D9531E" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#D9531E" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                <XAxis dataKey="date" fontSize={11} stroke="#94a3b8" />
                <YAxis fontSize={11} stroke="#94a3b8" domain={[80, 100]} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="attendance_pct" stroke="#D9531E" strokeWidth={2} fill="url(#attGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <button onClick={() => setDrill("attendance")} data-testid="drill-attendance-btn" className="mt-2 text-xs text-[color:var(--hl-orange)] hover:underline inline-flex items-center gap-1">
            Drill down <ChevronRight className="w-3 h-3" />
          </button>
        </div>

        <div className="hl-card p-5" data-testid="admissions-card">
          <div className="overline mb-1">Admissions pipeline</div>
          <div className="font-display font-bold text-lg tracking-tight mb-3">Live applications</div>
          <div className="h-48">
            <ResponsiveContainer>
              <BarChart data={pipelineBar}>
                <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                <XAxis dataKey="stage" fontSize={9} interval={0} angle={-25} textAnchor="end" height={60} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Bar dataKey="count" fill="#002147" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <button onClick={() => setDrill("admissions")} data-testid="drill-admissions-btn" className="mt-2 text-xs text-[color:var(--hl-orange)] hover:underline inline-flex items-center gap-1">
            Drill down <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Finance + Student Life */}
      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <div className="hl-card p-5" data-testid="finance-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="overline">Finance</div>
              <div className="font-display font-bold text-lg tracking-tight">Debtor age analysis</div>
            </div>
            <button onClick={() => setDrill("finance")} data-testid="drill-finance-btn" className="text-xs text-[color:var(--hl-orange)] hover:underline inline-flex items-center gap-1">
              Drill down <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          <div className="h-48">
            <ResponsiveContainer>
              <BarChart data={snap.finance.debtor_age_analysis || []}>
                <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                <XAxis dataKey="bucket" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip formatter={(v) => money(v, symbol)} />
                <Bar dataKey="amount" fill="#D9531E" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="text-xs text-slate-500 mt-2">
            Alert threshold: {money(snap.finance.outstanding_alert_threshold_usd, symbol)} — {snap.finance.outstanding_alert_count} accounts above
          </div>
        </div>

        <div className="hl-card p-5" data-testid="studentlife-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="overline">Student Life360</div>
              <div className="font-display font-bold text-lg tracking-tight">House leaderboard</div>
            </div>
            <button onClick={() => setDrill("studentlife")} data-testid="drill-life-btn" className="text-xs text-[color:var(--hl-orange)] hover:underline inline-flex items-center gap-1">
              Drill down <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          <div className="h-48">
            <ResponsiveContainer>
              <BarChart data={houseBar} layout="vertical">
                <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                <XAxis type="number" fontSize={11} />
                <YAxis type="category" dataKey="house" width={90} fontSize={11} />
                <Tooltip />
                <Bar dataKey="points" fill="#059669" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="text-xs text-slate-500 mt-2 flex gap-4">
            <span>{snap.studentlife.merits} merits</span>
            <span>{snap.studentlife.demerits} demerits</span>
            <span>{snap.studentlife.pastoral_open} pastoral open</span>
          </div>
        </div>
      </div>

      <DrillDrawer domain={drill} onClose={() => setDrill(null)} snapshot={snap} />
      {showConfig && (
        <ConfigDialog config={snap.config} onSave={saveConfig} onClose={() => setShowConfig(false)} />
      )}

      <div className="mt-6 text-[10px] uppercase tracking-widest text-slate-400 text-center" data-testid="metrics-source-label">
        Powered by Education360™ Central Metrics Service · Snapshot {new Date(snap.generated_at).toLocaleTimeString()}
      </div>
    </div>
  );
}

/**
 * Executive Intelligence Centre hero — institution-first pattern.
 * L1 Institution (largest, uppercase)  ·  L2 "Executive Intelligence Centre"
 * L3 Location · AY 2026 · Term · Role     L4 Greeting (subdued)
 * Identity strip: Zimbabwe · Cambridge & ZIMSEC · Boarding & Day School ·
 *                 Established 1998 · Executive Intelligence Active · Operational
 * Actions (Refresh · Thresholds) preserved on the right.
 */
function IntelligenceHero({ tenant, user, snapshot, onReload, onConfig }) {
  const hour = new Date().getHours();
  const daypart = hour < 12 ? "morning" : hour < 17 ? "afternoon" : "evening";
  const surname = (user?.full_name || "").split(" ").slice(-1)[0] || "Head";
  const roleLabel = ROLE_LABEL[user?.role] || "Executive";
  const location = [tenant?.city, tenant?.country].filter(Boolean).join(", ") || "Harare, Zimbabwe";
  const schoolName = (tenant?.name || "").toUpperCase();
  const established = tenant?.established_year || 1998;
  const snapshotTime = snapshot?.generated_at ? new Date(snapshot.generated_at).toLocaleTimeString() : "";

  const badges = [
    { icon: MapPin, label: tenant?.country || "Zimbabwe" },
    { icon: BookOpenCheck, label: "Cambridge & ZIMSEC" },
    { icon: Home, label: "Boarding & Day School" },
    { icon: GraduationCap, label: `Established ${established}` },
    { icon: Sparkles, label: "Executive Intelligence Active" },
    { icon: Activity, label: "Operational" },
  ];

  return (
    <section
      className="relative overflow-hidden rounded-2xl mb-6 shadow-[0_20px_60px_-30px_rgba(0,33,71,0.45)] border border-slate-200/70"
      data-testid="intelligence-hero"
      style={{ background: "linear-gradient(135deg, #001432 0%, #002147 45%, #04244d 100%)" }}
    >
      <div className="absolute inset-x-0 top-0 h-[3px]" aria-hidden
        style={{ background: "linear-gradient(90deg, transparent 0%, #D9531E 30%, #D9531E 70%, transparent 100%)" }} />
      <div className="absolute inset-0 pointer-events-none opacity-[0.14]" aria-hidden style={{
        backgroundImage:
          "radial-gradient(600px 240px at 12% 0%, rgba(217,83,30,0.65), transparent 60%), radial-gradient(500px 220px at 90% 100%, rgba(59,130,246,0.35), transparent 60%)",
      }} />

      <div className="relative px-6 sm:px-10 pt-9 sm:pt-11 pb-8 sm:pb-10">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div
              className="text-[10px] sm:text-[11px] uppercase tracking-[0.42em] font-semibold text-[color:var(--hl-orange)] mb-3"
              data-testid="hero-kicker"
            >
              Executive Intelligence Centre
            </div>

            <h1
              className="font-display font-black text-white tracking-[-0.01em] leading-[0.98] break-words"
              style={{ fontSize: "clamp(2rem, 5.4vw, 4rem)" }}
              data-testid="hero-institution"
            >
              {schoolName || "HIGHLANDS INTERNATIONAL COLLEGE"}
            </h1>

            <div
              className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-white/75 text-[13px] sm:text-sm"
              data-testid="hero-context"
            >
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-[color:var(--hl-orange)]" /> {location}
              </span>
              <span className="hidden sm:inline text-white/25">·</span>
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="w-3.5 h-3.5 text-[color:var(--hl-orange)]" /> Academic Year {tenant?.academic_year || "2026"}
              </span>
              <span className="hidden sm:inline text-white/25">·</span>
              <span>{tenant?.current_term || "Term"}</span>
              <span className="hidden sm:inline text-white/25">·</span>
              <span className="inline-flex items-center gap-1.5 uppercase tracking-widest text-[10px] font-semibold text-white/85">
                <ShieldCheck className="w-3 h-3 text-[color:var(--hl-orange)]" /> {roleLabel}
              </span>
            </div>

            <div className="mt-3 text-white/55 text-xs sm:text-[13px] italic" data-testid="hero-greeting">
              Good {daypart}, {user?.full_name || `Dr ${surname}`}. Every KPI, alert, forecast and briefing on this page draws from the Central Metrics Service.
            </div>
          </div>

          {/* Preserved actions — Refresh + Thresholds */}
          <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 flex-shrink-0">
            <button
              onClick={onReload}
              data-testid="btn-refresh-metrics"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-white/20 bg-white/[0.06] text-white/85 hover:bg-white/[0.12] transition"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button
              onClick={onConfig}
              data-testid="btn-config"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-white/20 bg-white/[0.06] text-white/85 hover:bg-white/[0.12] transition"
            >
              <Settings2 className="w-3.5 h-3.5" /> Thresholds
            </button>
          </div>
        </div>

        {/* Identity strip */}
        <div className="mt-6 flex flex-wrap gap-2" data-testid="hero-identity-strip">
          {badges.map(({ icon: Icon, label }, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-semibold text-white/85 border border-white/15 bg-white/[0.06] backdrop-blur-sm"
            >
              <Icon className="w-3 h-3 text-[color:var(--hl-orange)]" /> {label}
            </span>
          ))}
          {snapshotTime && (
            <span className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-semibold text-white/60 border border-white/10">
              <Sparkles className="w-3 h-3 text-[color:var(--hl-orange)]" /> Metrics Snapshot · {snapshotTime}
            </span>
          )}
        </div>
      </div>
    </section>
  );
}
